import React, { ReactElement, useEffect, useRef, useState } from "react";
import SocialBlock from "src/interactions/components/SocialBlock";
import { SocialFeedType } from "src/social-feed";
import { SocialPost } from "../SocialPost";
import "./SocialPostContainer.scss";
import { SpintrTypes } from "src/typings";
import classNames from "classnames";
import { InteractiveTextFormatOptions } from "src/utils";
import { setFocusCommentFieldForUberId } from "src/ui";
import { localize } from "src/l10n";
import { useDispatch } from "react-redux";
import SystemStatusFeedItem from "src/system-status/SystemStatusFeedItem";

interface ISocialPostContainerProps {
    identity?: any;
    isShare?: boolean;
    post: Spintr.ISocialPostBase;
    feedType?: SocialFeedType;
    onDoneEditing?: any;
    interactiveTextOptions?:    InteractiveTextFormatOptions | undefined;
    index?: number;
    focusPostId?: number;
}

interface IState {
    expanded: boolean;
}

function SocialPostContainer(props: ISocialPostContainerProps): ReactElement {
    const dispatch = useDispatch();
    const shouldFocusPost = props.focusPostId === props.post.Id;
    const [isHighlighted, setIsHighlighted] = useState<boolean>(shouldFocusPost);
    const postRef = useRef<HTMLDivElement>(null);
    const hasAppliedFocusRef = useRef<boolean>(false);
    const [state, setState] = useState<IState>({
        expanded: shouldFocusPost || props.feedType !== SocialFeedType.SystemStatus
    });

    const systemStatusPost = props.post.content.find((c) => c.Type === SpintrTypes.UberType.SystemStatus) as Spintr.ISocialSystemStatus;

    useEffect(() => {
        if (shouldFocusPost && !state.expanded) {
            setState((prevState) => ({ ...prevState, expanded: true }));
        }
    }, [shouldFocusPost]);

    useEffect(() => {
        if (!shouldFocusPost || !state.expanded || !postRef.current || hasAppliedFocusRef.current) {
            return;
        }

        hasAppliedFocusRef.current = true;
        setIsHighlighted(true);
        postRef.current.scrollIntoView({ behavior: "smooth", block: "center" });

        const timeoutId = window.setTimeout(() => {
            setIsHighlighted(false);
        }, 2500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [shouldFocusPost, state.expanded]);

    if (!!systemStatusPost && !state.expanded) {
        return (
            <SystemStatusFeedItem
                post={systemStatusPost}
                parentPost={props.post}
                interactiveTextOptions={props.interactiveTextOptions}
                isMinified={true}
                onExpand={() => {
                    setState((prevState) => ({ ...prevState, expanded: !prevState.expanded }));
                    dispatch(setFocusCommentFieldForUberId(props.post.Id));              
                }}
            />
        )
    }

    const commentPlaceholder: string | undefined = !!systemStatusPost && (localize("COMPOSER_QUESTION"));

    return (
        <div
            ref={postRef}
            className={classNames("SocialPostContainer visage-box-shadow start-page-flexible-padding", {
                "is-focused-post": isHighlighted,
            })}
        >
            <div className="post-wrapper">
                <SocialPost
                    post={props.post}
                    onDoneEditing={props.onDoneEditing}
                    feedType={props.feedType}
                    interactiveTextOptions={props.interactiveTextOptions}
                />
            </div>
            {!props.isShare &&
                !props.post.hidden &&
                (props.post.StatusType !== SpintrTypes.StatusType.SystemStatus ||
                    props.feedType === SocialFeedType.SystemStatus ||
                    props.feedType === undefined) && (
                        <SocialBlock
                            onCommentIconClick={() => { }}
                            commentPlaceholder={commentPlaceholder}
                            uberId={props.post.Id}
                            comments={props.post.comments}
                            likers={props.post.likers}
                            identity={props.identity}
                            sendToMixpanel={props.feedType === SocialFeedType.Combined}
                            enableReactions={true}
                            displayBigButtons={true}
                            isFeedPost={true}
                            targetCreatorId={props.post.UserId}
                            targetIsQuestion={props.post.StatusType === SpintrTypes.StatusType.Question}
                            mentionedUsersHasAccess={props.post.mentionedUsersHasAccess}
                            feedType={props.feedType}
                        />
            )}
        </div>
    );
}


export default SocialPostContainer;
